import type { ErrorCode, WorkerResult } from '../types'
import type {
  ConversionInput,
  ConversionWorkerRequest,
  ConversionWorkerResponse,
} from './protocol'

export type WorkerFactory = () => Worker
export type ProgressCallback = (phase: Extract<ConversionWorkerResponse, { type: 'progress' }>['phase']) => void
export const MAX_CONCURRENT_CONVERSIONS = 1
export const CONVERSION_TIMEOUT_MS = 60_000

interface PendingTask {
  resolve: (value: WorkerResult) => void
  onProgress?: ProgressCallback
  timeout: ReturnType<typeof setTimeout>
}

interface WorkerListeners {
  message: (event: MessageEvent<ConversionWorkerResponse>) => void
  error: (event: Event) => void
}

export class ConversionWorkerClient {
  private worker: Worker | null = null
  private workerListeners: WorkerListeners | null = null
  private readonly pending = new Map<string, PendingTask>()

  constructor(
    private readonly createWorker: WorkerFactory,
    private readonly conversionTimeoutMs = CONVERSION_TIMEOUT_MS,
  ) {}

  convert(input: ConversionInput, onProgress?: ProgressCallback): Promise<WorkerResult> {
    if (this.pending.size >= MAX_CONCURRENT_CONVERSIONS) {
      return Promise.resolve({ status: 'failed', code: 'memory-pressure' })
    }

    const worker = this.ensureWorker()
    return new Promise((resolve) => {
      const request: ConversionWorkerRequest = { type: 'convert', ...input }
      const timeout = setTimeout(() => this.handleTimeout(input.id), this.conversionTimeoutMs)
      this.pending.set(input.id, { resolve, onProgress, timeout })
      worker.postMessage(request, [input.buffer])
    })
  }

  cancel(id: string): void {
    this.worker?.postMessage({ type: 'cancel', id } satisfies ConversionWorkerRequest)
  }

  terminate(): void {
    this.disposeWorker()
    this.resolveAll('cancelled')
  }

  private ensureWorker(): Worker {
    if (this.worker) return this.worker

    const worker = this.createWorker()
    this.worker = worker
    this.workerListeners = {
      message: (event) => {
        if (this.worker === worker) this.handleMessage(event)
      },
      error: () => {
        if (this.worker === worker) this.handleError()
      },
    }
    worker.addEventListener('message', this.workerListeners.message)
    worker.addEventListener('error', this.workerListeners.error)
    return worker
  }

  private readonly handleMessage = (event: MessageEvent<ConversionWorkerResponse>): void => {
    const message = event.data
    const pending = this.pending.get(message.id)
    if (!pending) return

    if (message.type === 'progress') {
      pending.onProgress?.(message.phase)
      return
    }
    if (message.type === 'dimensions') return

    this.pending.delete(message.id)
    clearTimeout(pending.timeout)
    if (message.type === 'result') {
      pending.resolve({ status: 'succeeded', blob: message.result.blob, mimeType: message.result.mimeType })
      return
    }
    pending.resolve({ status: 'failed', code: message.error })
  }

  private handleError(): void {
    this.disposeWorker()
    this.resolveAll('decoder-init-failed')
  }

  private readonly handleTimeout = (id: string): void => {
    const pending = this.pending.get(id)
    if (!pending) return

    this.pending.delete(id)
    this.disposeWorker()
    pending.resolve({ status: 'failed', code: 'corrupt-or-unreadable' })
  }

  private disposeWorker(): void {
    const worker = this.worker
    const listeners = this.workerListeners
    this.worker = null
    this.workerListeners = null
    if (!worker) return

    if (listeners) {
      worker.removeEventListener('message', listeners.message)
      worker.removeEventListener('error', listeners.error)
    }
    worker.terminate()
  }

  private resolveAll(code: ErrorCode): void {
    this.pending.forEach(({ resolve, timeout }) => {
      clearTimeout(timeout)
      resolve({ status: 'failed', code })
    })
    this.pending.clear()
  }
}
